import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Flex, Text, Button, Select, IconButton, Separator, Spinner } from "@radix-ui/themes";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../context/ToastContext";
import { ToastAlert } from "components";
import { useProfileContext, useLocationContext } from "context";
import { useGetGoogleLocations, useSaveGoogleLocationMappings } from "hooks";

export function GoogleReviewsConfigSetupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setToastMessage } = useToast();
  const { orgId, isManaging } = location.state || {};
  const { userProfile } = useProfileContext();
  const { initialItems } = useLocationContext();

  // Use orgId from state, or fall back to userProfile
  const organisationId = orgId || userProfile?.organisation_id;

  // States
  const [googleLocations, setGoogleLocations] = useState([]);
  const [locationMappings, setLocationMappings] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Fetch Google Business Profile locations
  const { data: googleLocationsData, isLoading: isLocationsLoading, error: locationsError } = useGetGoogleLocations(
    organisationId,
    !!organisationId
  );

  const saveLocationMappings = useSaveGoogleLocationMappings();

  // Update locations when API data is available
  useEffect(() => {
    if (googleLocationsData?.data?.status === "success" && googleLocationsData?.data?.accounts) {
      // Flatten all locations from all accounts
      const allLocations = [];
      googleLocationsData.data.accounts.forEach((account) => {
        if (account.locations && Array.isArray(account.locations)) {
          account.locations.forEach((loc) => {
            // Format address from storefrontAddress
            let formattedAddress = "";
            if (loc.storefrontAddress) {
              const addr = loc.storefrontAddress;
              const parts = [];
              if (addr.addressLines) parts.push(...addr.addressLines);
              if (addr.locality) parts.push(addr.locality);
              if (addr.postalCode) parts.push(addr.postalCode);
              formattedAddress = parts.join(", ");
            }

            allLocations.push({
              name: `${account.account_id}/${loc.name}`, // Full path: accounts/xxx/locations/yyy
              title: loc.title || loc.name,
              address: formattedAddress,
              accountId: account.account_id.replace("accounts/", ""),
              locationId: loc.name.replace("locations/", ""),
              placeId: loc.metadata?.placeId || "",
            });
          });
        }
      });
      setGoogleLocations(allLocations);
      console.log("Parsed Google locations:", allLocations);
    }
  }, [googleLocationsData]);

  const handleGoogleLocationChange = (bconnectLocationId, googleLocationName) => {
    if (googleLocationName === "none") {
      // Remove the mapping
      const newMappings = { ...locationMappings };
      delete newMappings[bconnectLocationId];
      setLocationMappings(newMappings);
    } else {
      setLocationMappings({
        ...locationMappings,
        [bconnectLocationId]: googleLocationName,
      });
    }
  };

  const handleSaveAndFinish = async () => {
    try {
      setIsLoading(true);

      // Prepare location mappings data
      const mappingsToSave = Object.entries(locationMappings)
        .map(([bconnectLocationId, googleLocationName]) => {
          const googleLoc = googleLocations.find((loc) => loc.name === googleLocationName);
          if (!googleLoc) return null;

          return {
            location_id: parseInt(bconnectLocationId),
            google_location_name: googleLoc.title,
            google_location_address: googleLoc.address || "",
            google_business_account_id: `accounts/${googleLoc.accountId}`,
            google_business_location_id: `locations/${googleLoc.locationId}`,
            google_place_id: googleLoc.placeId || "",
          };
        })
        .filter(Boolean);

      if (mappingsToSave.length === 0) {
        setToastMessage("Please map at least one location");
        setIsLoading(false);
        return;
      }

      // Call the save-mappings API
      const result = await saveLocationMappings.mutateAsync({
        organisation_id: organisationId,
        mappings: mappingsToSave,
      });

      if (result?.data?.status !== "success") {
        throw new Error(result?.data?.message || "Failed to save mappings");
      }

      // Invalidate the Google Reviews status query
      queryClient.invalidateQueries(["google-reviews-status"]);
      queryClient.invalidateQueries(["google-locations"]);

      // Navigate to success page
      navigate("/activation-success", {
        state: {
          description: "Your Google Business Profile locations are now connected. Reviews will be synced automatically.",
          integrationType: "google-reviews",
        },
      });
    } catch (error) {
      console.error("Error during setup:", error);
      setToastMessage(error.message || "Failed to complete Google Reviews setup");
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state
  if (!organisationId) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: "100vh" }}>
        <Text>No organisation data found. Please go back and try again.</Text>
      </Flex>
    );
  }

  return (
    <Flex
      style={{
        minHeight: "100vh",
      }}
      justify="center"
      align="center"
      p="6"
    >
      <Card
        style={{
          maxWidth: "900px",
          width: "100%",
        }}
        p="4"
      >
        <Flex style={{ alignSelf: "stretch" }} display="flex" direction="column" align="start">
          {/* Header */}
          <Flex style={{ alignSelf: "stretch" }} display="flex" justify="between" align="center">
            <Flex display="flex" align="center" gap="4">
              <Flex
                style={{
                  borderRadius: "var(--space-1)",
                  background: "#FFFFFF",
                  border: "1px solid var(--gray-a6)",
                }}
                height="40px"
                width="40px"
                p="2"
                justify="center"
                align="center"
              >
                <img src={require("../../../assets/images/google.png")} alt="Google Reviews" style={{ width: "24px" }} />
              </Flex>
              <Flex direction="column" justify="center" align="start">
                <Text size="4" weight="medium">
                  Google Reviews
                </Text>
                <Text style={{ color: "var(--gray-a11)" }} size="1">
                  Customer reviews
                </Text>
              </Flex>
            </Flex>
            <Flex display="flex" align="center" gap="4">
              <Button size="2" variant="soft" onClick={() => navigate("/integrations/browse-all")} color="gray">
                Cancel
              </Button>
              <Button size="2" variant="soft" onClick={handleSaveAndFinish} loading={isLoading}>
                Save and finish
              </Button>
            </Flex>
          </Flex>

          <Separator orientation="horizontal" size="4" my="4" />

          {/* Locations Section */}
          <Flex style={{ alignSelf: "stretch" }} display="flex" align="start" gap="5">
            <Flex style={{ alignSelf: "stretch" }} display="flex" width="280px" direction="column" align="start" gap="2">
              <Text size="2" weight="medium">
                Location Mapping
              </Text>
              <Text size="1" weight="regular" style={{ color: "var(--gray-a11)" }}>
                Map each b connect location to its corresponding Google Business Profile location. You can add or remove mappings later.
              </Text>
            </Flex>

            <Flex direction="column" gap="3" style={{ width: "100%" }}>
              {/* Loading state */}
              {isLocationsLoading && (
                <Flex justify="center" align="center" py="6">
                  <Spinner size="2" />
                  <Text size="2" color="gray" style={{ marginLeft: "8px" }}>
                    Loading Google locations...
                  </Text>
                </Flex>
              )}

              {/* Error state */}
              {locationsError && (
                <Flex justify="center" align="center" py="6">
                  <Text size="2" color="red">
                    Failed to load Google locations. Please try again.
                  </Text>
                </Flex>
              )}

              {/* No locations found */}
              {!isLocationsLoading && !locationsError && googleLocations.length === 0 && (
                <Flex justify="center" align="center" py="6">
                  <Text size="2" color="gray">
                    No Google Business Profile locations found. Please make sure your account has locations set up.
                  </Text>
                </Flex>
              )}

              {/* Locations mapping */}
              {!isLocationsLoading && googleLocations.length > 0 && (
                <>
                  {/* Header row */}
                  <Flex direction="row" justify="between" style={{ width: "100%" }}>
                    <Text size="2" weight="medium" style={{ width: "45%" }}>
                      b connect location
                    </Text>
                    <Text size="2" weight="medium" style={{ width: "45%", paddingLeft: "40px" }}>
                      Google Business Profile location
                    </Text>
                  </Flex>

                  {/* Location mappings */}
                  {(initialItems || []).map((loc) => (
                    <Flex key={loc.value} direction="row" justify="between" align="center" style={{ width: "100%" }}>
                      <Flex style={{ width: "45%", minWidth: 0 }}>
                        <Flex
                          style={{
                            width: "100%",
                            backgroundColor: "var(--gray-3)",
                            border: "1px solid var(--gray-a7)",
                            borderRadius: "var(--radius-2)",
                            padding: "0 var(--space-2)",
                            height: "34px",
                            alignItems: "center",
                            overflow: "hidden",
                          }}
                        >
                          <Text
                            size="2"
                            title={loc.label}
                            style={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              width: "100%",
                              color: "var(--gray-a11)",
                            }}
                          >
                            {loc.label}
                          </Text>
                        </Flex>
                      </Flex>

                      <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                        <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                          <ArrowRightIcon width="16" height="16" />
                        </IconButton>
                      </Flex>

                      <Flex style={{ width: "45%", minWidth: 0, maxWidth: "45%", flex: "0 0 45%" }}>
                        <Select.Root
                          value={locationMappings[loc.value] || ""}
                          onValueChange={(value) => handleGoogleLocationChange(loc.value, value)}
                          style={{ width: "100%" }}
                        >
                          <Select.Trigger
                            placeholder="Select a Google location..."
                            style={{
                              width: "100%",
                              maxWidth: "100%",
                              overflow: "hidden",
                              boxSizing: "border-box",
                            }}
                          >
                            <div
                              style={{
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                width: "100%",
                                display: "block",
                                maxWidth: "100%",
                              }}
                              title={
                                locationMappings[loc.value]
                                  ? googleLocations.find((g) => g.name === locationMappings[loc.value])?.title || ""
                                  : "Select a Google location"
                              }
                            >
                              {locationMappings[loc.value]
                                ? googleLocations.find((g) => g.name === locationMappings[loc.value])?.title || ""
                                : "Select a Google location"}
                            </div>
                          </Select.Trigger>
                          <Select.Content side="bottom" align="start" sideOffset={4}>
                            <Select.Item value="none">
                              <Text color="gray">-- No mapping --</Text>
                            </Select.Item>
                            {googleLocations.map((googleLoc) => (
                              <Select.Item key={googleLoc.name} value={googleLoc.name} title={googleLoc.title}>
                                <Flex direction="column">
                                  <Text size="2">{googleLoc.title}</Text>
                                  {googleLoc.address && (
                                    <Text size="1" color="gray">
                                      {googleLoc.address}
                                    </Text>
                                  )}
                                </Flex>
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Root>
                      </Flex>
                    </Flex>
                  ))}
                </>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Card>

      <div style={{ position: "fixed", zIndex: 2147483647, pointerEvents: "none" }}>
        <ToastAlert />
      </div>
    </Flex>
  );
}
